import React, { useState } from 'react';
import { YouTubeService } from '@/lib/services/youtube';
import { OutputDisplay } from '@/components/output/output-display';

interface TranscriptResponse {
    text: string;
    duration: number;
    offset: number;
}

interface VideoDetails {
    title: string;
    description: string;
    channelTitle: string;
    publishedAt: string;
    viewCount: string;
    likeCount: string;
    duration: string;
}

const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;
const RAPIDAPI_KEY = import.meta.env.VITE_RAPIDAPI_KEY;

const YouTubeServiceTest: React.FC = () => {
    const defaultVideoId = 'dQw4w9WgXcQ';
    const [videoId, setVideoId] = useState(defaultVideoId);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<TranscriptResponse[] | null>(null);
    const [videoDetails, setVideoDetails] = useState<VideoDetails | null>(null);
    const [videoLoading, setVideoLoading] = useState(false);
    const [videoError, setVideoError] = useState<string | null>(null);
    const [mcqResult, setMcqResult] = useState<any>(null);
    const [mcqLoading, setMcqLoading] = useState(false);
    const [mcqError, setMcqError] = useState<string | null>(null);
    const [glossaryResult, setGlossaryResult] = useState<any>(null);
    const [isGeneratingGlossary, setIsGeneratingGlossary] = useState(false);
    const [transcript, setTranscript] = useState<string | null>(null);
    const [bookData, setBookData] = useState<any>(null);

    const testTranscriptAPI = async () => {
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const url = `https://youtube-transcript3.p.rapidapi.com/api/transcript-with-url?url=${encodeURIComponent(`https://www.youtube.com/watch?v=${videoId}`)}&flat_text=true&lang=en`;
            console.log('Fetching transcript from:', url);
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-RapidAPI-Key': RAPIDAPI_KEY,
                    'X-RapidAPI-Host': 'youtube-transcript3.p.rapidapi.com'
                }
            });

            console.log('Response status:', response.status);
            const responseText = await response.text();
            console.log('Raw response:', responseText);

            if (!response.ok) {
                throw new Error(`API Error: ${response.status} - ${responseText}`);
            }

            const data = JSON.parse(responseText);
            setResult(data);
        } catch (err) {
            console.error('API Error:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    const testYouTubeDataAPI = async () => {
        if (!YOUTUBE_API_KEY) {
            setVideoError('YouTube API key is not configured');
            return;
        }

        setVideoLoading(true);
        setVideoError(null);
        setVideoDetails(null);

        try {
            const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet,statistics,contentDetails&id=${videoId}&key=${YOUTUBE_API_KEY}`;
            console.log('Fetching video details from:', url);

            const response = await fetch(url);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(`API Error: ${response.status} - ${JSON.stringify(data)}`);
            }

            if (!data.items || data.items.length === 0) {
                throw new Error('Video not found');
            }

            const video = data.items[0];
            setVideoDetails({
                title: video.snippet.title,
                description: video.snippet.description,
                channelTitle: video.snippet.channelTitle,
                publishedAt: video.snippet.publishedAt,
                viewCount: video.statistics.viewCount,
                likeCount: video.statistics.likeCount,
                duration: video.contentDetails.duration
            });
        } catch (err) {
            console.error('YouTube Data API Error:', err);
            setVideoError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setVideoLoading(false);
        }
    };

    const testMCQGeneration = async () => {
        try {
            setMcqLoading(true);
            setMcqError(null);
            setMcqResult(null);

            // Process video with MCQ generation
            const videoData = await YouTubeService.processVideo(videoId, {
                generateMCQ: true
            });

            setMcqResult(videoData.mcq);
            setTranscript(videoData.transcript);

        } catch (error) {
            console.error('MCQ Generation Error:', error);
            setMcqError(error instanceof Error ? error.message : 'Unknown error occurred');
        } finally {
            setMcqLoading(false);
        }
    };

    const testGlossaryGeneration = async () => {
        setIsGeneratingGlossary(true);
        try {
            // Process video with glossary generation
            const videoData = await YouTubeService.processVideo(videoId, {
                generateGlossary: true
            });

            setGlossaryResult(videoData.glossary);
            setTranscript(videoData.transcript);
        } catch (error) {
            console.error('Error generating glossary:', error);
            setError(error instanceof Error ? error.message : 'Failed to generate glossary');
        } finally {
            setIsGeneratingGlossary(false);
        }
    };

    const testBookGeneration = async () => {
        try {
            // Process video with book generation
            const videoData = await YouTubeService.processVideo(videoId, {
                generateBook: true
            });

            setBookData(videoData.book);
            setTranscript(videoData.transcript);
        } catch (error) {
            console.error('Error generating book:', error);
            setError(error instanceof Error ? error.message : 'Failed to generate book');
        }
    };

    // Helper function to format duration
    const formatDuration = (duration: string) => {
        const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
        if (!match) return duration;

        const hours = match[1] ? match[1].replace('H', '') : '0';
        const minutes = match[2] ? match[2].replace('M', '') : '0';
        const seconds = match[3] ? match[3].replace('S', '') : '0';

        const parts = [];
        if (parseInt(hours) > 0) parts.push(`${hours}h`);
        if (parseInt(minutes) > 0) parts.push(`${minutes}m`);
        if (parseInt(seconds) > 0) parts.push(`${seconds}s`);

        return parts.join(' ');
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-2xl mx-auto">
                <h1 className="text-3xl font-bold mb-8">YouTube Service Test</h1>
                
                {/* Input and buttons section */}
                <div className="space-y-4 mb-8">
                    <input
                        type="text"
                        value={videoId}
                        onChange={(e) => setVideoId(e.target.value)}
                        placeholder="Enter YouTube Video ID"
                        className="w-full px-4 py-2 border rounded"
                    />
                    
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={testTranscriptAPI}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                            {loading ? 'Loading...' : 'Test Transcript API'}
                        </button>
                        
                        <button
                            onClick={testYouTubeDataAPI}
                            disabled={videoLoading}
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                        >
                            {videoLoading ? 'Loading...' : 'Test YouTube Data API'}
                        </button>
                        
                        <button
                            onClick={testMCQGeneration}
                            disabled={mcqLoading}
                            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
                        >
                            {mcqLoading ? 'Generating...' : 'Generate MCQs'}
                        </button>

                        <button
                            onClick={testGlossaryGeneration}
                            disabled={isGeneratingGlossary}
                            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50"
                        >
                            {isGeneratingGlossary ? 'Generating...' : 'Generate Glossary'}
                        </button>

                        <button
                            onClick={testBookGeneration}
                            disabled={!transcript}
                            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-50"
                        >
                            Generate Book
                        </button>
                    </div>
                </div>

                {/* Output Display */}
                <OutputDisplay
                    mcqData={mcqResult}
                    glossaryData={glossaryResult}
                    bookData={bookData}
                />
            </div>
        </div>
    );
};

export default YouTubeServiceTest; 